import { FeatureFlag } from '@ps/web-core-enums';
import { useFeature } from '@ps/web-core-store';
import { useIsSSR } from '@ps/web-core-utils';
import React, { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import Url from 'url-parse';

import type { Props } from '@swf/components/Iframe/Iframe';
import Iframe from '@swf/components/Iframe/Iframe';
import { dispatch, select } from '@swf/core/store';

export interface IdpMessageData {
  origin: string;
  data: {
    access_token?: string;
    token_type?: string;
  };
}

const IdpClient = () => {
  const isPartnerSite = useSelector(select.cms.isExternalPartnerSite);
  const locationHref = useSelector(select.location.getHref);
  const { apiDomain, idpClientUrl } = useSelector(select.config.get);
  const locationUrl = new Url(locationHref, true);
  const { code } = locationUrl.query;

  const { pathname, origin } = locationUrl;

  if (!idpClientUrl && !apiDomain) {
    return null;
  }

  const iframeSrcUrl = new URL(idpClientUrl || `${apiDomain}/oauth2/client`);

  if (code) {
    iframeSrcUrl.searchParams.append('code', code);
  }

  if (isPartnerSite) {
    iframeSrcUrl.searchParams.append('redirect_uri', origin + (pathname === '/' ? '' : pathname));
  }

  return (
    <Iframe
      title="IDP Client"
      id="idp-client"
      src={iframeSrcUrl.toString()}
      style={{ display: 'none' }}
    />
  );
};

const IdpClientProvider: React.FC<Props> = ({ children }) => {
  const locationHref = useSelector(select.location.getHref);
  const locationUrl = new Url(locationHref, true);
  const { replace } = useHistory();
  const isSSR = useIsSSR();
  const isPartnerSite = useSelector(select.cms.isExternalPartnerSite);
  const { token: sessionToken } = useSelector(select.session.get);
  const idpSecondaryLoginEnabled = useFeature(FeatureFlag.IdpSecondaryLoginEnabled);
  const { code } = locationUrl.query;
  const isEmbedded = useSelector(select.device.isEmbedded);
  const isAuthenticated = useSelector(select.session.isClientAuthenticated);

  const idpMessageListener = useCallback(
    async ({ data }: IdpMessageData) => {
      if (data.access_token && data.token_type === 'Bearer') {
        if (isPartnerSite) {
          dispatch.session.update({ idpToken: data.access_token });
          if (!isAuthenticated) {
            await dispatch.auth.verifySession();
            if (code) {
              const newUrl = new URL(locationHref);
              newUrl.searchParams.delete('code');
              replace({ search: newUrl.searchParams.toString() });
            } else {
              dispatch.cms.fetchPage({ href: window.location.href });
            }
          }
        } else if (sessionToken) {
          dispatch.session.update({ idpToken: data.access_token });
        }
      }
    },
    [code, isPartnerSite, isAuthenticated, locationHref, replace, sessionToken]
  );

  useEffect(() => {
    window.addEventListener('message', idpMessageListener);
    return () => window.removeEventListener('message', idpMessageListener);
  }, [idpMessageListener]);

  const shouldRenderIdpClient =
    !isSSR && (isPartnerSite || idpSecondaryLoginEnabled) && !isEmbedded;

  return (
    <>
      {children}
      {shouldRenderIdpClient && <IdpClient />}
    </>
  );
};

export default IdpClientProvider;
